import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import { createAssessment, addQuestionsToAssessment } from '../../api/assessmentsApi';
import { getQuestions } from '../../api/questionsApi';
import CustomAlertModal from '../../components/common/CustomAlertModal';

const AssessmentBuilderPage = () => {
  const { driveId } = useParams();
  const navigate = useNavigate();
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  
  const [step, setStep] = useState(1);
  const [assessmentId, setAssessmentId] = useState(null);
  const [questionDistribution, setQuestionDistribution] = useState({ easy: 0, medium: 0, hard: 0, coding: 0, total: 0 });
  const [generationMode, setGenerationMode] = useState('STATIC');

  const watchEasy = watch('easy', 0);
  const watchMedium = watch('medium', 0);
  const watchHard = watch('hard', 0);
  const watchCoding = watch('coding', 0);
  const totalQuestions = Number(watchEasy || 0) + Number(watchMedium || 0) + Number(watchHard || 0) + Number(watchCoding || 0);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [alertConfig, setAlertConfig] = useState({ show: false, message: '', onConfirm: null });

  const showCustomAlert = (message, onConfirm = null) => {
    setAlertConfig({ show: true, message, onConfirm });
  };

  const hideCustomAlert = () => {
    const { onConfirm } = alertConfig;
    setAlertConfig({ show: false, message: '', onConfirm: null });
    if (onConfirm) onConfirm();
  };

  const [bankQuestions, setBankQuestions] = useState([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [selectionMode, setSelectionMode] = useState('Manual Selection');

  const [filterCategory, setFilterCategory] = useState('All');
  const [filterDifficulty, setFilterDifficulty] = useState('All');

  const categories = ['All', ...new Set(bankQuestions.map(q => q.category))];

  const filteredQuestions = bankQuestions.filter(q => {
    if (filterCategory !== 'All' && q.category !== filterCategory) return false;
    if (filterDifficulty !== 'All' && q.difficulty !== filterDifficulty) return false;
    return true;
  });

  const getSelectedCount = (difficulty) => {
    return selectedQuestionIds.filter(id => {
      const q = bankQuestions.find(bq => bq.id === id);
      return q && q.type !== 'CODING' && q.difficulty.toLowerCase() === difficulty.toLowerCase();
    }).length;
  };

  const getSelectedCodingCount = () => {
    return selectedQuestionIds.filter(id => {
      const q = bankQuestions.find(bq => bq.id === id);
      return q && q.type === 'CODING';
    }).length;
  };

  const clearSelection = () => {
    setSelectedQuestionIds([]);
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

 const fetchQuestions = async () => {
  try {
    const res = await getQuestions();

    console.log("Questions Response:", res);

    // axiosClient already unwraps response.data
    setBankQuestions(Array.isArray(res.data) ? res.data : []);
  } catch (err) {
    console.error("Failed to fetch global questions", err);
    setErrorMsg("Failed to load questions.");
  }
};

const onSubmitStep1 = async (data) => {
  setIsSubmitting(true);
  setSuccessMsg("");
  setErrorMsg("");

  if (totalQuestions <= 0) {
    showCustomAlert("Please configure at least one question before continuing.");
    setIsSubmitting(false);
    return;
  }

  setQuestionDistribution({
    easy: Number(data.easy || 0),
    medium: Number(data.medium || 0),
    hard: Number(data.hard || 0),
    coding: Number(data.coding || 0),
    total: totalQuestions
  });

  try {
    const payload = {
      driveId,
      name: data.name,
      durationMinutes: Number(data.durationMinutes),
      totalMarks: Number(data.totalMarks),
      registrationOpen: data.registrationOpen === "true",
      isPublished: data.isPublished === "true",
      generationMode,
      easyCount: generationMode === 'DYNAMIC' ? Number(data.easy || 0) : 0,
      mediumCount: generationMode === 'DYNAMIC' ? Number(data.medium || 0) : 0,
      hardCount: generationMode === 'DYNAMIC' ? Number(data.hard || 0) : 0,
      codingCount: generationMode === 'DYNAMIC' ? Number(data.coding || 0) : 0,
    };

    if (generationMode === 'DYNAMIC') {
      const easyAvailable = bankQuestions.filter(q => q.type !== 'CODING' && (q.difficulty || '').toLowerCase() === 'easy').length;
      const mediumAvailable = bankQuestions.filter(q => q.type !== 'CODING' && (q.difficulty || '').toLowerCase() === 'medium').length;
      const hardAvailable = bankQuestions.filter(q => q.type !== 'CODING' && (q.difficulty || '').toLowerCase() === 'hard').length;

      if (payload.easyCount > easyAvailable) {
        showCustomAlert(`Not enough Easy questions available. Requested: ${payload.easyCount}, Available: ${easyAvailable}`);
        setIsSubmitting(false);
        return;
      }
      if (payload.mediumCount > mediumAvailable) {
        showCustomAlert(`Not enough Medium questions available. Requested: ${payload.mediumCount}, Available: ${mediumAvailable}`);
        setIsSubmitting(false);
        return;
      }
      if (payload.hardCount > hardAvailable) {
        showCustomAlert(`Not enough Hard questions available. Requested: ${payload.hardCount}, Available: ${hardAvailable}`);
        setIsSubmitting(false);
        return;
      }
      const codingAvailable = bankQuestions.filter(q => q.type === 'CODING').length;
      if (payload.codingCount > codingAvailable) {
        showCustomAlert(`Not enough Coding questions available. Requested: ${payload.codingCount}, Available: ${codingAvailable}`);
        setIsSubmitting(false);
        return;
      }
    }

    console.log("Submitting Payload:", payload);

    const res = await createAssessment(payload);

    console.log("Create Assessment Response:", res);

    const assessment = res.data;

    if (!assessment || !assessment.id) {
      throw new Error("Assessment ID not found in response.");
    }

    setAssessmentId(assessment.id);
    
    if (generationMode === 'DYNAMIC') {
      showCustomAlert('Dynamic Assessment created successfully!', () => {
        navigate('/admin/drives');
      });
    } else {
      setSuccessMsg("Assessment created successfully! Now select questions.");
      setStep(2);
    }
  } catch (err) {
    console.error("Create Assessment Error:", err);

    setErrorMsg(
      err.response?.data?.message ||
      err.message ||
      "Failed to create assessment."
    );
  } finally {
    setIsSubmitting(false);
  }
};
  const toggleQuestionSelection = (id) => {
    if (selectedQuestionIds.includes(id)) {
      setSelectedQuestionIds(prev => prev.filter(qId => qId !== id));
    } else {
      const q = bankQuestions.find(bq => bq.id === id);
      if (q) {
        const diff = q.difficulty.toLowerCase();
        if (q.type === 'CODING') {
          if (getSelectedCodingCount() >= questionDistribution.coding) {
            return showCustomAlert("You have already selected the required number of Coding questions.");
          }
        } else if (diff === 'easy' && getSelectedCount('easy') >= questionDistribution.easy) {
          return showCustomAlert("You have already selected the required number of Easy questions.");
        }
        else if (diff === 'medium' && getSelectedCount('medium') >= questionDistribution.medium) {
          return showCustomAlert("You have already selected the required number of Medium questions.");
        }
        else if (diff === 'hard' && getSelectedCount('hard') >= questionDistribution.hard) {
          return showCustomAlert("You have already selected the required number of Hard questions.");
        }
        if (selectedQuestionIds.length >= questionDistribution.total) {
          return showCustomAlert("You have already selected the required Total Questions.");
        }
        setSelectedQuestionIds(prev => [...prev, id]);
      }
    }
  };

  const toggleSelectAll = () => {
    if (filteredQuestions.length === 0) return;
    const allDisplayedIds = filteredQuestions.map(q => q.id);
    const allSelected = allDisplayedIds.every(id => selectedQuestionIds.includes(id));
    
    if (allSelected) {
      setSelectedQuestionIds(prev => prev.filter(id => !allDisplayedIds.includes(id)));
    } else {
      let newSelection = [...selectedQuestionIds];
      let eCount = getSelectedCount('easy');
      let mCount = getSelectedCount('medium');
      let hCount = getSelectedCount('hard');
      let cCount = getSelectedCodingCount();
      let tCount = newSelection.length;

      for (const id of allDisplayedIds) {
        if (!newSelection.includes(id)) {
          const q = bankQuestions.find(bq => bq.id === id);
          if (q) {
            const diff = q.difficulty.toLowerCase();
            const isCoding = q.type === 'CODING';
            if (isCoding && cCount >= questionDistribution.coding) continue;
            if (!isCoding && diff === 'easy' && eCount >= questionDistribution.easy) continue;
            if (!isCoding && diff === 'medium' && mCount >= questionDistribution.medium) continue;
            if (!isCoding && diff === 'hard' && hCount >= questionDistribution.hard) continue;
            if (tCount >= questionDistribution.total) break;

            newSelection.push(id);
            if (isCoding) cCount++;
            else if (diff === 'easy') eCount++;
            else if (diff === 'medium') mCount++;
            else if (diff === 'hard') hCount++;
            tCount++;
          }
        }
      }
      setSelectedQuestionIds(newSelection);
    }
  };

  const handleAutoSelect = () => {
    // Determine target counts based on required distribution
    const requiredEasy = questionDistribution.easy;
    const requiredMedium = questionDistribution.medium;
    const requiredHard = questionDistribution.hard;
    const requiredCoding = questionDistribution.coding || 0;

    // Filter available questions by difficulty from the currently displayed/filtered ones
    const availableEasy = filteredQuestions.filter(q => q.type !== 'CODING' && q.difficulty.toLowerCase() === 'easy');
    const availableMedium = filteredQuestions.filter(q => q.type !== 'CODING' && q.difficulty.toLowerCase() === 'medium');
    const availableHard = filteredQuestions.filter(q => q.type !== 'CODING' && q.difficulty.toLowerCase() === 'hard');
    const availableCoding = filteredQuestions.filter(q => q.type === 'CODING');

    let errors = [];
    if (availableEasy.length < requiredEasy) {
      errors.push(`Only ${availableEasy.length} Easy questions are available.`);
    }
    if (availableMedium.length < requiredMedium) {
      errors.push(`Only ${availableMedium.length} Medium questions are available.`);
    }
    if (availableHard.length < requiredHard) {
      errors.push(`Only ${availableHard.length} Hard questions are available.`);
    }

    if (availableCoding.length < requiredCoding) {
      errors.push(`Only ${availableCoding.length} Coding questions are available.`);
    }

    if (errors.length > 0) {
      showCustomAlert(errors.join(' '));
      return;
    }

    // Shuffle arrays
    const shuffle = (array) => {
      const arr = [...array];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    };

    const selectedEasy = shuffle(availableEasy).slice(0, requiredEasy).map(q => q.id);
    const selectedMedium = shuffle(availableMedium).slice(0, requiredMedium).map(q => q.id);
    const selectedHard = shuffle(availableHard).slice(0, requiredHard).map(q => q.id);

    // Completely replace selection to ensure no duplicates and perfectly match requested distribution
    const selectedCoding = shuffle(availableCoding).slice(0, requiredCoding).map(q => q.id);
    setSelectedQuestionIds([...selectedEasy, ...selectedMedium, ...selectedHard, ...selectedCoding]);
  };

  const handleSaveQuestions = async () => {
    setIsSubmitting(true);
    try {
      await addQuestionsToAssessment(assessmentId, selectedQuestionIds);
      showCustomAlert('Assessment fully configured and questions mapped!', () => {
        navigate('/admin/drives');
      });
    } catch (err) {
      showCustomAlert('Failed to map questions.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid p-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold mb-1">Build Assessment {step === 2 && '- Select Questions'}</h3>
          <p className="text-muted mb-0">{step === 1 ? 'Step 1: Set up a new assessment' : 'Step 2: Map questions from the Master Question Bank'}</p>
        </div>
      </div>

      {successMsg && <div className="alert alert-success">{successMsg}</div>}
      {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}

      <div className="card custom-card border-0 shadow-sm rounded-4">
        <div className="card-body p-4 p-md-5">
          {step === 1 && (
            <form onSubmit={handleSubmit(onSubmitStep1)}>
              <h5 className="text-primary-custom fw-bold mb-4 border-bottom pb-2">Assessment Details</h5>
              <div className="row g-4 mb-5">
                <div className="col-md-12">
                  <label className="form-label fw-semibold">Assessment Name <span className="text-danger">*</span></label>
                  <input type="text" className={`form-control p-2 ${errors.name ? 'is-invalid' : ''}`} {...register('name', { required: true })} placeholder="e.g. Technical Round 1" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Duration (Minutes) <span className="text-danger">*</span></label>
                  <input type="number" className={`form-control p-2 ${errors.durationMinutes ? 'is-invalid' : ''}`} {...register('durationMinutes', { required: true, min: 1 })} defaultValue="60" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Total Marks <span className="text-danger">*</span></label>
                  <input type="number" className={`form-control p-2 ${errors.totalMarks ? 'is-invalid' : ''}`} {...register('totalMarks', { required: true, min: 1 })} defaultValue="100" />
                </div>
                
                <div className="col-md-12 mt-4">
                  <h6 className="fw-bold text-secondary mb-3">Assessment Generation Mode</h6>
                  <div className="d-flex gap-4 mb-4">
                    <div className="form-check">
                      <input 
                        className="form-check-input" 
                        type="radio" 
                        name="generationMode" 
                        id="staticMode"
                        value="STATIC"
                        checked={generationMode === 'STATIC'}
                        onChange={(e) => setGenerationMode(e.target.value)}
                      />
                      <label className="form-check-label fw-medium" htmlFor="staticMode">
                        Static Paper (Same for all students)
                      </label>
                    </div>
                    <div className="form-check">
                      <input 
                        className="form-check-input" 
                        type="radio" 
                        name="generationMode" 
                        id="dynamicMode"
                        value="DYNAMIC"
                        checked={generationMode === 'DYNAMIC'}
                        onChange={(e) => setGenerationMode(e.target.value)}
                      />
                      <label className="form-check-label fw-medium" htmlFor="dynamicMode">
                        Dynamic Randomized Paper (Unique per student)
                      </label>
                    </div>
                  </div>

                  {generationMode === 'DYNAMIC' && (
                    <div className="alert alert-info">
                      <strong>Dynamic Mode:</strong> Students will receive a randomly generated, unique paper based on the distribution below. No manual question selection is needed.
                    </div>
                  )}

                  <h6 className="fw-bold text-secondary mb-3">Question Distribution</h6>
                  <div className="row g-3 bg-light p-3 rounded">
                    <div className="col-md-3">
                      <label className="form-label fw-semibold">Easy Questions</label>
                      <input type="number" className="form-control p-2" {...register('easy', { min: 0 })} defaultValue="0" min="0" />
                      {generationMode === 'DYNAMIC' && (
                        <small className="text-muted d-block mt-1">Available Pool: {bankQuestions.filter(q => q.type !== 'CODING' && (q.difficulty || '').toLowerCase() === 'easy').length}</small>
                      )}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold">Medium Questions</label>
                      <input type="number" className="form-control p-2" {...register('medium', { min: 0 })} defaultValue="0" min="0" />
                      {generationMode === 'DYNAMIC' && (
                        <small className="text-muted d-block mt-1">Available Pool: {bankQuestions.filter(q => q.type !== 'CODING' && (q.difficulty || '').toLowerCase() === 'medium').length}</small>
                      )}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold">Hard Questions</label>
                      <input type="number" className="form-control p-2" {...register('hard', { min: 0 })} defaultValue="0" min="0" />
                      {generationMode === 'DYNAMIC' && (
                        <small className="text-muted d-block mt-1">Available Pool: {bankQuestions.filter(q => q.type !== 'CODING' && (q.difficulty || '').toLowerCase() === 'hard').length}</small>
                      )}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold">Coding Questions</label>
                      <input type="number" className="form-control p-2" {...register('coding', { min: 0 })} defaultValue="0" min="0" />
                      <small className="text-muted d-block mt-1">Available Pool: {bankQuestions.filter(q => q.type === 'CODING').length}</small>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-bold text-primary">Total Questions</label>
                      <input type="number" className="form-control p-2 bg-white" value={totalQuestions} readOnly />
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Registration Open</label>
                  <select className="form-select p-2" {...register('registrationOpen')}>
                    <option value="false">Closed</option>
                    <option value="true">Open</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Publish Status</label>
                  <select className="form-select p-2" {...register('isPublished')}>
                    <option value="false">Draft</option>
                    <option value="true">Published</option>
                  </select>
                </div>
              </div>

              <div className="d-flex justify-content-end gap-3 border-top pt-4">
                <button type="button" className="btn btn-light px-4 rounded-pill fw-bold" onClick={() => navigate(-1)}>Cancel</button>
                <button type="submit" className="btn btn-primary-custom px-5 rounded-pill fw-bold shadow-sm" disabled={isSubmitting}>
                  {isSubmitting ? 'Proceeding...' : (generationMode === 'DYNAMIC' ? 'Save & Finish Assessment' : 'Next: Select Questions')}
                </button>
              </div>
            </form>
          )}

          {step === 2 && (
            <div>
              <div className="mb-4">
                <h5 className="fw-bold text-secondary mb-3 border-bottom pb-2">Question Distribution</h5>
                <div className="row text-center g-2">
                  <div className="col-md">
                    <div className="p-3 bg-light border rounded">
                      <h6 className="fw-bold text-success mb-2">Easy</h6>
                      <div className="d-flex justify-content-between text-muted small">
                        <span>Required: {questionDistribution.easy}</span>
                        <span>Selected: <strong className="text-dark">{getSelectedCount('easy')}</strong></span>
                        <span>Remaining: {Math.max(0, questionDistribution.easy - getSelectedCount('easy'))}</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md">
                    <div className="p-3 bg-light border rounded">
                      <h6 className="fw-bold text-warning mb-2">Medium</h6>
                      <div className="d-flex justify-content-between text-muted small">
                        <span>Required: {questionDistribution.medium}</span>
                        <span>Selected: <strong className="text-dark">{getSelectedCount('medium')}</strong></span>
                        <span>Remaining: {Math.max(0, questionDistribution.medium - getSelectedCount('medium'))}</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md">
                    <div className="p-3 bg-light border rounded">
                      <h6 className="fw-bold text-danger mb-2">Hard</h6>
                      <div className="d-flex justify-content-between text-muted small">
                        <span>Required: {questionDistribution.hard}</span>
                        <span>Selected: <strong className="text-dark">{getSelectedCount('hard')}</strong></span>
                        <span>Remaining: {Math.max(0, questionDistribution.hard - getSelectedCount('hard'))}</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md">
                    <div className="p-3 bg-light border rounded">
                      <h6 className="fw-bold text-info mb-2">Coding</h6>
                      <div className="d-flex justify-content-between text-muted small">
                        <span>Required: {questionDistribution.coding}</span>
                        <span>Selected: <strong className="text-dark">{getSelectedCodingCount()}</strong></span>
                        <span>Remaining: {Math.max(0, questionDistribution.coding - getSelectedCodingCount())}</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md">
                    <div className="p-3 bg-primary bg-opacity-10 border border-primary rounded">
                      <h6 className="fw-bold text-primary mb-2">Total Questions</h6>
                      <h2 className="mb-0 text-primary">{selectedQuestionIds.length}</h2>
                      <div className="d-flex justify-content-between text-muted small">
                        <span>Required: {questionDistribution.total}</span>
                        <span>Remaining: {Math.max(0, questionDistribution.total - selectedQuestionIds.length)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h5 className="fw-bold text-secondary mb-3 border-bottom pb-2">Question Selection Mode</h5>
                <div className="d-flex gap-4 mb-3">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="radio" 
                      name="selectionMode" 
                      id="manualSelect"
                      value="Manual Selection"
                      checked={selectionMode === 'Manual Selection'}
                      onChange={(e) => setSelectionMode(e.target.value)}
                    />
                    <label className="form-check-label fw-medium" htmlFor="manualSelect">
                      Manual Selection
                    </label>
                  </div>
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="radio" 
                      name="selectionMode" 
                      id="autoSelect"
                      value="Automatic Selection"
                      checked={selectionMode === 'Automatic Selection'}
                      onChange={(e) => setSelectionMode(e.target.value)}
                    />
                    <label className="form-check-label fw-medium" htmlFor="autoSelect">
                      Automatic Selection
                    </label>
                  </div>
                </div>
                {selectionMode === 'Automatic Selection' && (
                  <button 
                    type="button" 
                    className="btn btn-outline-primary fw-bold px-4" 
                    onClick={handleAutoSelect}
                  >
                    Auto Select Questions
                  </button>
                )}
              </div>

              <div className="d-flex justify-content-between mb-3 align-items-center">
                <h5 className="fw-bold mb-0">Master Question Bank</h5>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-sm btn-outline-danger me-2 fw-bold" onClick={clearSelection} disabled={selectedQuestionIds.length === 0}>Clear Selection</button>
                  <select className="form-select form-select-sm" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                    {categories.map((cat, idx) => <option key={idx} value={cat}>{cat === 'All' ? 'All Categories ▼' : cat}</option>)}
                  </select>
                  <select className="form-select form-select-sm" value={filterDifficulty} onChange={(e) => setFilterDifficulty(e.target.value)}>
                    <option value="All">All Difficulties ▼</option>
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>
              </div>
              <div className="table-responsive mb-4" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="table table-hover align-middle">
                  <thead className="table-light sticky-top">
                    <tr>
                          <th style={{ width: '50px' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              aria-label="Select all questions"
                              onChange={toggleSelectAll}
                              checked={filteredQuestions.length > 0 && filteredQuestions.every(q => selectedQuestionIds.includes(q.id))}
                              disabled={filteredQuestions.length === 0}
                            />
                          </th>
                      <th>Question</th>
                      <th>Type</th>
                      <th>Category</th>
                      <th>Difficulty</th>
                      <th>Marks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredQuestions.map(q => (
                      <tr key={q.id} style={{ cursor: 'pointer' }} onClick={() => toggleQuestionSelection(q.id)}>
                        <td>
                          <input 
                            type="checkbox" 
                            className="form-check-input"
                            checked={selectedQuestionIds.includes(q.id)}
                            readOnly
                          />
                        </td>
                        <td className="fw-medium">{q.text}</td>
                        <td><span className="badge bg-light text-dark border">{q.type}</span></td>
                        <td>{q.category}</td>
                        <td>{q.difficulty}</td>
                        <td><span className="text-success fw-bold">+{q.marks}</span> / <span className="text-danger">-{q.negativeMarks}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="d-flex justify-content-end gap-3 border-top pt-4">
                <button type="button" className="btn btn-primary-custom px-5 rounded-pill fw-bold shadow-sm" onClick={handleSaveQuestions} disabled={isSubmitting || selectedQuestionIds.length === 0}>
                  {isSubmitting ? 'Saving...' : 'Save & Finish Assessment'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <CustomAlertModal show={alertConfig.show} message={alertConfig.message} onHide={hideCustomAlert} />
    </div>
  );
};

export default AssessmentBuilderPage;

